import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import 'iconify-icon';
import { STATES_LIST, resolveState } from '../data/brazil-state-centroids';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * FranchiseFullList
 *
 * Companion list below the FranchiseLocator on the /franquias page.
 *
 * Behavior (per product spec):
 * - HIDDEN by default — only renders a small CTA strip with a
 *   "Ver todas as franquias" button.
 * - EXPANDED automatically when the URL has `?state=XX` (visitor came
 *   from the home page with a state pre-selected) OR `?expanded=1`.
 * - When expanded: shows state chips, the full filterable card grid,
 *   and a "Recolher lista" button to collapse back.
 * - Expansion triggers a smooth scroll to the section start.
 *
 * The map (FranchiseLocator) above is the protagonist; this list is
 * complementary.
 */
const FranchiseFullList = () => {
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionRef = useRef(null);

  const stateFilter = (searchParams.get('state') || '').toUpperCase();
  // Only the explicit `expanded=1` flag opens the list. Having a `state`
  // alone (e.g., from the locator filter sync) is NOT enough — selecting
  // a state in the map shouldn't surprise-expand the list. The home
  // page link bundles `state` + `expanded=1` together, so users who came
  // from there still see the list pre-expanded.
  const expanded = searchParams.get('expanded') === '1';

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/public/franchises`);
        setFranchises(Array.isArray(data) ? data : []);
      } catch {
        setFranchises([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Smooth-scroll to this section whenever it transitions to expanded.
  // Delayed by 250ms because the Locator above contains an SVG map that
  // takes time to render and shifts the page layout (which would make
  // an immediate scrollIntoView land at the wrong y-offset).
  useEffect(() => {
    if (!expanded || !sectionRef.current) return;
    const t = window.setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 250);
    return () => window.clearTimeout(t);
  }, [expanded]);

  const availableStates = useMemo(() => {
    const set = new Set();
    franchises.forEach((f) => {
      const st = resolveState(f.state);
      if (st) set.add(st.code);
    });
    return STATES_LIST.filter((s) => set.has(s.code));
  }, [franchises]);

  const filtered = useMemo(() => {
    if (!stateFilter) return franchises;
    return franchises.filter((f) => {
      const st = resolveState(f.state);
      return st?.code === stateFilter;
    });
  }, [franchises, stateFilter]);

  const sorted = useMemo(
    () =>
      [...filtered].sort((a, b) => {
        const sa = a.state || '';
        const sb = b.state || '';
        if (sa !== sb) return sa.localeCompare(sb);
        return (a.city || '').localeCompare(b.city || '', 'pt-BR');
      }),
    [filtered],
  );

  const openList = () => {
    const next = new URLSearchParams(searchParams);
    next.set('expanded', '1');
    setSearchParams(next, { replace: false });
  };

  const closeList = () => {
    // Only collapse the list — keep the locator's state/city filter
    // intact (the user may still want to see the map filtered).
    const next = new URLSearchParams(searchParams);
    next.delete('expanded');
    setSearchParams(next, { replace: false });
    // Scroll back up to the map so the user sees they collapsed it.
    document
      .querySelector('section[data-testid="franchise-locator"], #franchises')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const setStateFilter = (code) => {
    const next = new URLSearchParams(searchParams);
    if (code) next.set('state', code);
    else next.delete('state');
    next.delete('city');
    // Always keep the list open while interacting with chips
    next.set('expanded', '1');
    setSearchParams(next, { replace: false });
  };

  // Collapsed mode — just the CTA strip.
  if (!expanded) {
    return (
      <section
        ref={sectionRef}
        id="franchise-full-list"
        className="relative z-10 py-16 border-t border-neutral-900"
        style={{ background: 'rgba(10, 10, 10, 0.78)' }}
        data-testid="franchise-full-list"
      >
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-3 block">
            Toda a rede
          </span>
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#EAEAEA] mb-3">
            Quer ver a lista completa de franquias?
          </h2>
          <p className="text-sm md:text-base text-neutral-400 mb-7 max-w-2xl mx-auto leading-[1.7]">
            {loading
              ? 'Carregando…'
              : `Veja as ${franchises.length} unidades certificadas no Brasil em formato de lista — com nome, cidade, estado e contato direto.`}
          </p>
          <button
            type="button"
            onClick={openList}
            data-testid="franchise-list-expand"
            className="inline-flex items-center gap-2 px-7 py-3.5 bg-accent-orange text-white rounded-full text-sm font-bold uppercase tracking-wider hover:bg-[#E55D00] transition-colors shadow-lg shadow-accent-orange/20"
          >
            Ver todas as franquias
            <iconify-icon icon="solar:arrow-down-linear"></iconify-icon>
          </button>
        </div>
      </section>
    );
  }

  // Expanded mode — chips + grid + recolher button.
  return (
    <section
      ref={sectionRef}
      id="franchise-full-list"
      className="relative z-10 py-20 border-t border-neutral-900"
      style={{ background: 'rgba(10, 10, 10, 0.78)' }}
      data-testid="franchise-full-list"
    >
      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-10 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-3 block">
              Todas as unidades
            </span>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#EAEAEA]">
              {stateFilter
                ? `Franquias em ${stateFilter}`
                : 'Lista completa de franquias'}
            </h2>
            <p className="text-sm text-neutral-500 mt-2">
              {loading
                ? 'Carregando…'
                : `${sorted.length} ${sorted.length === 1 ? 'unidade' : 'unidades'}.`}
            </p>
          </div>

          {/* Right side: chips + close button */}
          <div className="flex flex-col gap-3 items-start md:items-end">
            <button
              type="button"
              onClick={closeList}
              data-testid="franchise-list-collapse"
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#333333] text-neutral-400 rounded-full text-xs font-mono uppercase tracking-widest hover:border-accent-orange hover:text-accent-orange transition-colors"
            >
              <iconify-icon icon="solar:arrow-up-linear"></iconify-icon>
              Recolher lista
            </button>
            <div className="flex flex-wrap gap-2 max-w-2xl">
              <button
                type="button"
                onClick={() => setStateFilter('')}
                data-testid="franchise-list-filter-all"
                className={`px-3.5 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${
                  !stateFilter
                    ? 'bg-accent-orange text-white'
                    : 'bg-[#1F1F1F] text-neutral-400 border border-[#333333] hover:border-accent-orange/60'
                }`}
              >
                Todos
              </button>
              {availableStates.map((s) => (
                <button
                  key={s.code}
                  type="button"
                  onClick={() => setStateFilter(s.code)}
                  data-testid={`franchise-list-filter-${s.code.toLowerCase()}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${
                    stateFilter === s.code
                      ? 'bg-accent-orange text-white'
                      : 'bg-[#1F1F1F] text-neutral-400 border border-[#333333] hover:border-accent-orange/60'
                  }`}
                >
                  {s.code}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Grid */}
        {!loading && sorted.length === 0 && (
          <div
            className="text-center py-16 border border-dashed border-[#333333] rounded-2xl"
            data-testid="franchise-list-empty"
          >
            <iconify-icon icon="solar:shop-2-linear" className="text-5xl text-neutral-700 mb-3"></iconify-icon>
            <p className="text-neutral-500">Nenhuma franquia encontrada nesse estado.</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {sorted.map((f) => {
            const insta = (f.instagram || '').replace(/^@/, '');
            const instaUrl = insta ? `https://instagram.com/${insta}` : null;
            return (
              <article
                key={f.franchise_id}
                data-testid={`franchise-card-${f.franchise_id}`}
                className="group bg-[#1A1A1A] border border-[#333333] rounded-2xl p-6 hover:border-accent-orange/60 transition-all duration-300 flex flex-col relative"
              >
                {/* Full-card link — overlays the article so anywhere
                    outside the Instagram/WhatsApp anchors navigates to
                    the profile page. The anchor tags below use `relative
                    z-10` to sit above and keep their own click targets. */}
                <Link
                  to={`/franquia/${f.franchise_id}`}
                  data-testid={`franchise-card-link-${f.franchise_id}`}
                  aria-label={`Ver perfil de ${f.name}`}
                  className="absolute inset-0 rounded-2xl focus:outline-none focus:ring-2 focus:ring-accent-orange"
                />
                <div className="flex items-start justify-between mb-4 relative">
                  <div className="w-10 h-10 rounded-full bg-accent-orange/15 border border-accent-orange/30 flex items-center justify-center text-accent-orange flex-shrink-0">
                    <iconify-icon icon="solar:shop-2-linear" className="text-lg"></iconify-icon>
                  </div>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-accent-orange/80">
                    {f.state}
                  </span>
                </div>

                <h3 className="text-lg font-semibold text-[#EAEAEA] mb-1 leading-tight group-hover:text-accent-orange transition-colors relative">
                  {f.name}
                </h3>
                {f.coverage_description && (
                  <p
                    className="text-xs mb-1.5 relative"
                    style={{ color: '#FF6700' }}
                    data-testid={`franchise-card-coverage-${f.franchise_id}`}
                  >
                    {f.coverage_description}
                  </p>
                )}
                <p className="text-sm text-neutral-400 mb-4 flex items-center gap-1.5 relative">
                  <iconify-icon icon="solar:map-point-linear" className="text-neutral-500"></iconify-icon>
                  {f.city || '—'}
                </p>

                <div className="mt-auto pt-4 border-t border-[#262626] flex flex-wrap items-center gap-3 relative z-10">
                  {instaUrl && (
                    <a
                      href={instaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`franchise-card-instagram-${f.franchise_id}`}
                      className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-accent-orange transition-colors"
                    >
                      <iconify-icon icon="mdi:instagram" className="text-base"></iconify-icon>
                      @{insta}
                    </a>
                  )}
                  {f.whatsapp && (
                    <a
                      href={`https://wa.me/${(f.whatsapp || '').replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-accent-orange transition-colors"
                    >
                      <iconify-icon icon="mdi:whatsapp" className="text-base"></iconify-icon>
                      WhatsApp
                    </a>
                  )}
                  <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-accent-orange opacity-0 group-hover:opacity-100 transition-opacity">
                    Ver perfil
                    <iconify-icon icon="solar:arrow-right-linear"></iconify-icon>
                  </span>
                </div>
              </article>
            );
          })}
        </div>

        {/* Bottom-of-list collapse shortcut */}
        <div className="text-center mt-10">
          <button
            type="button"
            onClick={closeList}
            data-testid="franchise-list-collapse-bottom"
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#333333] text-neutral-400 rounded-full text-xs font-mono uppercase tracking-widest hover:border-accent-orange hover:text-accent-orange transition-colors"
          >
            <iconify-icon icon="solar:arrow-up-linear"></iconify-icon>
            Recolher lista
          </button>
        </div>
      </div>
    </section>
  );
};

export default FranchiseFullList;
